import Phaser from 'phaser';
import lottie from 'lottie-web';
import { GAME_CONFIG } from '../config/gameConfig';
import { callGameEnd, callGameSave } from '../utils/gamebox';
import {
  ENCYCLOPEDIA_DATA_KEY,
  serializeDiscoveredIds,
} from '../utils/encyclopedia';

interface ItemData {
  id: number;
  sprite: Phaser.GameObjects.Image;
  isTarget: boolean;
}

interface PlacedItem {
  x: number;
  y: number;
  scale: number;
  isTarget?: boolean;
  onTopOfTarget?: boolean; // ターゲットの上に重なっている場合true
}

export class GameScene extends Phaser.Scene {
  private score: number = 0;
  private timeLeft: number = GAME_CONFIG.TIME_LIMIT;
  private items: ItemData[] = [];
  private targetIds: number[] = [];
  private foundTargets: number = 0;
  private discoveredIdsThisSession: Set<number> = new Set();
  private currentItemsPerStage: number = 5; // 5からスタート、クリアごとに+10

  // UI要素
  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private soundToggleBtn!: Phaser.GameObjects.Image;
  private stageText!: Phaser.GameObjects.Text;
  private targetContainer!: Phaser.GameObjects.Container;
  private timerEvent!: Phaser.Time.TimerEvent;
  private bgmSound: Phaser.Sound.BaseSound | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(): void {
    // ゲーム状態の初期化
    this.score = 0;
    this.timeLeft = GAME_CONFIG.TIME_LIMIT;
    this.items = [];
    this.targetIds = [];
    this.foundTargets = 0;
    this.discoveredIdsThisSession = new Set();
    this.currentItemsPerStage = 5;
  }

  create(): void {
    // サウンド設定を適用（BGM再生前に）
    let soundEnabled = this.registry.get('soundEnabled');
    if (soundEnabled === undefined) {
      try {
        const stored = localStorage.getItem('soundEnabled');
        soundEnabled = stored !== 'false';
      } catch {
        soundEnabled = true;
      }
      this.registry.set('soundEnabled', soundEnabled);
    }
    this.sound.mute = !soundEnabled;

    // BGM再生（スタートタップでゲーム開始した直後）
    this.bgmSound = this.sound.add('bgm', { loop: true });
    this.bgmSound.play();

    // 背景
    this.createBackground();

    // ターゲットを選択
    this.selectTargets();

    // アイテムを配置
    this.placeItems();

    // UIを作成
    this.createUI();

    // タイマー開始
    this.startTimer();
  }

  update(): void {
    // 残り10秒以下でBGMのピッチを上げる（毎フレーム適用で確実に反映）
    if (this.bgmSound && this.bgmSound.isPlaying) {
      const targetRate = this.timeLeft <= 10 ? 1.15 : 1;
      const s = this.bgmSound as Phaser.Sound.HTML5AudioSound;
      if (s.rate !== targetRate) {
        s.rate = targetRate;
      }
    }
  }

  private createBackground(): void {
    const { WIDTH, HEIGHT, COLORS } = GAME_CONFIG;

    const graphics = this.add.graphics();
    graphics.fillStyle(COLORS.WHITE, 1);
    graphics.fillRect(0, 0, WIDTH, HEIGHT);

    // バウハウス風グリッド（幾何学的）
    graphics.lineStyle(1, COLORS.GRAY, 0.2);
    for (let x = 0; x < WIDTH; x += 48) {
      graphics.lineBetween(x, 0, x, HEIGHT);
    }
    for (let y = 0; y < HEIGHT; y += 48) {
      graphics.lineBetween(0, y, WIDTH, y);
    }
  }

  private selectTargets(): void {
    const { TOTAL_ITEM_TYPES, TARGET_COUNT } = GAME_CONFIG;

    // ランダムにターゲットを選択
    const availableIds = Array.from(
      { length: TOTAL_ITEM_TYPES },
      (_, i) => i + 1
    );
    Phaser.Utils.Array.Shuffle(availableIds);

    this.targetIds = availableIds.slice(0, TARGET_COUNT);
  }

  private placeItems(): void {
    const { WIDTH, HEIGHT, TOTAL_ITEM_TYPES, ITEM_MIN_SCALE, ITEM_MAX_SCALE } =
      GAME_CONFIG;
    const itemsPerStage = this.currentItemsPerStage;

    // 配置エリア（ヘッダーを避ける）
    const minY = 270;
    const maxY = HEIGHT - 50;
    const minX = 50;
    const maxX = WIDTH - 50;

    // 配置済みアイテムを記録（位置・スケール）
    const placedItems: PlacedItem[] = [];
    // ターゲットアイテムを別途記録（保護対象）
    const targetItems: PlacedItem[] = [];

    // ターゲットアイテムを配置（各ターゲット1つずつ）
    for (const targetId of this.targetIds) {
      const scale = Phaser.Math.FloatBetween(ITEM_MIN_SCALE, ITEM_MAX_SCALE);
      const pos = this.getRandomPosition(
        minX,
        maxX,
        minY,
        maxY,
        placedItems,
        scale,
        targetItems
      );

      // ターゲットは必ず配置（nullの場合は中央に配置）
      const finalPos = pos || { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

      const sprite = this.add.image(finalPos.x, finalPos.y, `item_${targetId}`);
      sprite.setScale(scale);
      sprite.setInteractive({ useHandCursor: true });
      sprite.setDepth(2); // 常に最前面でクリックしやすく

      // ターゲットは残り30秒以下で動き出す
      this.addTargetAnimationIfTimeUp(sprite, scale);

      this.items.push({
        id: targetId,
        sprite,
        isTarget: true,
      });

      const placedItem = { x: finalPos.x, y: finalPos.y, scale, isTarget: true };
      placedItems.push(placedItem);
      targetItems.push(placedItem);

      // クリックイベント
      sprite.on('pointerdown', () => this.onItemClick(sprite, targetId, true));
    }

    // 残りのアイテム（ダミー）を配置
    const remainingCount = itemsPerStage - GAME_CONFIG.TARGET_COUNT;
    const availableIds = Array.from(
      { length: TOTAL_ITEM_TYPES },
      (_, i) => i + 1
    ).filter((id) => !this.targetIds.includes(id));

    // フェイクの70%が動く。例: 20体→フェイク17→動く12体
    const animateCount = Math.max(1, Math.round(remainingCount * 0.7));
    const shuffledIndices = Phaser.Utils.Array.Shuffle([...Array(remainingCount).keys()]);
    const animateIndices = new Set(shuffledIndices.slice(0, animateCount));
    // そのうち一部はターゲット同様の大袈裟な動き
    const bigAnimCount = Math.max(1, Math.floor(animateCount * 0.3));
    const bigAnimIndices = new Set(shuffledIndices.slice(0, bigAnimCount));

    for (let i = 0; i < remainingCount; i++) {
      const itemId = Phaser.Utils.Array.GetRandom(availableIds);
      const scale = Phaser.Math.FloatBetween(ITEM_MIN_SCALE, ITEM_MAX_SCALE);
      const pos = this.getRandomPosition(
        minX,
        maxX,
        minY,
        maxY,
        placedItems,
        scale,
        targetItems
      );

      if (!pos) continue;

      const sprite = this.add.image(pos.x, pos.y, `item_${itemId}`);
      sprite.setScale(scale);
      sprite.setInteractive({ useHandCursor: true });
      sprite.setDepth(pos.onTopOfTarget ? 3 : 1);

      // ダミーの70%が動く。そのうち30%はターゲット同様の大袈裟な動き
      if (animateIndices.has(i)) {
        if (bigAnimIndices.has(i)) {
          this.addTargetAnimation(sprite, scale);
        } else {
          this.addItemAnimation(sprite, scale);
        }
      }

      this.items.push({
        id: itemId,
        sprite,
        isTarget: false,
      });

      placedItems.push({
        x: pos.x,
        y: pos.y,
        scale,
        isTarget: false,
        onTopOfTarget: pos.onTopOfTarget,
      });

      // クリックイベント
      sprite.on('pointerdown', () =>
        this.onItemClick(sprite, itemId, false)
      );
    }
  }

  /** ダミーアイテム用のアニメーション（控えめ） */
  private addItemAnimation(
    sprite: Phaser.GameObjects.Image,
    baseScale: number
  ): void {
    const roll = Phaser.Math.Between(0, 11);
    const animType = roll === 10 ? 6 : roll === 11 ? 7 : roll % 6;
    const delay = Phaser.Math.Between(0, 1000);

    switch (animType) {
      case 0:
        // 横揺れ（小）
        this.tweens.add({
          targets: sprite,
          x: sprite.x + Phaser.Math.Between(-8, 8),
          duration: Phaser.Math.Between(1500, 2500),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 1:
        // 上下（小）
        this.tweens.add({
          targets: sprite,
          y: sprite.y + Phaser.Math.Between(-6, 6),
          duration: Phaser.Math.Between(1200, 2000),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 2:
        // 微振動
        this.tweens.add({
          targets: sprite,
          x: sprite.x + Phaser.Math.Between(-2, 2),
          y: sprite.y + Phaser.Math.Between(-2, 2),
          duration: 80,
          yoyo: true,
          repeat: -1,
          delay,
        });
        break;
      case 3:
        // 呼吸（スケール微変化）
        this.tweens.add({
          targets: sprite,
          scaleX: baseScale * 1.03,
          scaleY: baseScale * 1.03,
          duration: Phaser.Math.Between(1500, 2500),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 4:
        // 回転揺れ（小）
        this.tweens.add({
          targets: sprite,
          angle: Phaser.Math.Between(-3, 3),
          duration: Phaser.Math.Between(1000, 1800),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 5:
        // 点滅（緩やか）
        this.tweens.add({
          targets: sprite,
          alpha: 0.4,
          duration: 900,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 6:
        // ピューんと上に飛ぶ（低確率・2秒おき・飛ぶ前にブルブル）
        const baseY = sprite.y;
        const baseX = sprite.x;
        const flyUp = () => {
          // ブルブル（約200ms）
          this.tweens.add({
            targets: sprite,
            x: baseX + 4,
            duration: 35,
            yoyo: true,
            repeat: 3,
            onComplete: () => {
              sprite.x = baseX;
              // ピューんと飛んで画面外
              this.tweens.add({
                targets: sprite,
                y: -150,
                duration: 600,
                ease: 'Quad.easeOut',
                onComplete: () => {
                  sprite.y = baseY;
                  this.time.delayedCall(3000, flyUp);
                },
              });
            },
          });
        };
        this.time.delayedCall(delay, flyUp);
        break;
      case 7:
        // 画面を横切る
        const dist = Phaser.Math.Between(150, 250) * (Phaser.Math.Between(0, 1) ? 1 : -1);
        this.tweens.add({
          targets: sprite,
          x: sprite.x + dist,
          duration: Phaser.Math.Between(2500, 4000),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
    }
  }

  /** ターゲットアイテム用のアニメーション（大きめ・目立つ） */
  private addTargetAnimation(
    sprite: Phaser.GameObjects.Image,
    baseScale: number
  ): void {
    const roll = Phaser.Math.Between(0, 11);
    const animType = roll === 10 ? 6 : roll === 11 ? 7 : roll % 6;
    const delay = Phaser.Math.Between(0, 500);

    switch (animType) {
      case 0:
        // 大きく横揺れ
        this.tweens.add({
          targets: sprite,
          x: sprite.x + Phaser.Math.Between(-20, 20),
          duration: Phaser.Math.Between(800, 1200),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 1:
        // ぴょんぴょん跳ねる
        this.tweens.add({
          targets: sprite,
          y: sprite.y - 25,
          duration: 400,
          yoyo: true,
          repeat: -1,
          ease: 'Quad.easeOut',
          delay,
        });
        break;
      case 2:
        // ぶるぶる震える
        this.tweens.add({
          targets: sprite,
          x: sprite.x + 5,
          duration: 50,
          yoyo: true,
          repeat: -1,
          delay,
        });
        this.tweens.add({
          targets: sprite,
          y: sprite.y + 3,
          duration: 70,
          yoyo: true,
          repeat: -1,
          delay: delay + 25,
        });
        break;
      case 3:
        // パルス（大きく膨らむ）
        this.tweens.add({
          targets: sprite,
          scaleX: baseScale * 1.15,
          scaleY: baseScale * 1.15,
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 4:
        // 回転揺れ（大）+ 上下
        this.tweens.add({
          targets: sprite,
          angle: Phaser.Math.Between(-8, 8),
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        this.tweens.add({
          targets: sprite,
          y: sprite.y - 10,
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay: delay + 100,
        });
        break;
      case 5:
        // 点滅（大袈裟・緩やか）
        this.tweens.add({
          targets: sprite,
          alpha: 0.2,
          duration: 550,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
      case 6:
        // ピューんと上に飛ぶ（低確率・2秒おき・飛ぶ前にブルブル）
        const baseY = sprite.y;
        const baseX = sprite.x;
        const flyUp = () => {
          // ブルブル（約200ms）
          this.tweens.add({
            targets: sprite,
            x: baseX + 6,
            duration: 30,
            yoyo: true,
            repeat: 3,
            onComplete: () => {
              sprite.x = baseX;
              // ピューんと飛んで画面外
              this.tweens.add({
                targets: sprite,
                y: -180,
                duration: 450,
                ease: 'Quad.easeOut',
                onComplete: () => {
                  sprite.y = baseY;
                  this.time.delayedCall(3000, flyUp);
                },
              });
            },
          });
        };
        this.time.delayedCall(delay, flyUp);
        break;
      case 7:
        // 画面を横切る（大袈裟・幅広く）
        const dist = Phaser.Math.Between(200, 300) * (Phaser.Math.Between(0, 1) ? 1 : -1);
        this.tweens.add({
          targets: sprite,
          x: sprite.x + dist,
          duration: Phaser.Math.Between(1800, 2800),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay,
        });
        break;
    }
  }

  /** 2つの矩形の重なり面積を計算 */
  private getOverlapArea(
    x1: number,
    y1: number,
    w1: number,
    h1: number,
    x2: number,
    y2: number,
    w2: number,
    h2: number
  ): number {
    const left = Math.max(x1 - w1 / 2, x2 - w2 / 2);
    const right = Math.min(x1 + w1 / 2, x2 + w2 / 2);
    const top = Math.max(y1 - h1 / 2, y2 - h2 / 2);
    const bottom = Math.min(y1 + h1 / 2, y2 + h2 / 2);
    if (left >= right || top >= bottom) return 0;
    return (right - left) * (bottom - top);
  }

  private getRandomPosition(
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    placedItems: PlacedItem[],
    placeScale: number,
    targetItems: PlacedItem[]
  ): { x: number; y: number; onTopOfTarget?: boolean } | null {
    const { ITEM_WIDTH, ITEM_HEIGHT, MAX_OVERLAP_RATIO, TARGET_MAX_OVERLAP_RATIO } =
      GAME_CONFIG;
    const placeW = ITEM_WIDTH * placeScale;
    const placeH = ITEM_HEIGHT * placeScale;
    const placeArea = placeW * placeH;

    let attempts = 0;
    const maxAttempts = 2000;

    let bestPosition: { x: number; y: number; onTopOfTarget?: boolean } | null = null;
    let bestNewItemOverlap = Infinity;

    while (attempts < maxAttempts) {
      const x = Phaser.Math.Between(minX, maxX);
      const y = Phaser.Math.Between(minY, maxY);

      // ターゲットと重なる場合：50%で上に重ねる（60%未満）、50%で下に重ねる（制限なし）
      const newOverlapOnTarget = targetItems.reduce((sum, t) => {
        const tw = ITEM_WIDTH * t.scale;
        const th = ITEM_HEIGHT * t.scale;
        return sum + this.getOverlapArea(x, y, placeW, placeH, t.x, t.y, tw, th);
      }, 0);
      const overlapsTarget = newOverlapOnTarget > 0;
      let onTopOfTarget = false;

      if (overlapsTarget) {
        onTopOfTarget = Phaser.Math.FloatBetween(0, 1) < 0.5;
        if (onTopOfTarget) {
          // 上に重ねる場合：60%未満チェック
          let targetsOK = true;
          for (const target of targetItems) {
            const targetW = ITEM_WIDTH * target.scale;
            const targetH = ITEM_HEIGHT * target.scale;
            const targetArea = targetW * targetH;
            const maxTargetOverlap = targetArea * TARGET_MAX_OVERLAP_RATIO;
            let totalOverlapOnTarget = 0;
            for (const item of placedItems) {
              if (item.isTarget || !item.onTopOfTarget) continue;
              const itemW = ITEM_WIDTH * item.scale;
              const itemH = ITEM_HEIGHT * item.scale;
              totalOverlapOnTarget += this.getOverlapArea(
                target.x, target.y, targetW, targetH,
                item.x, item.y, itemW, itemH
              );
            }
            const overlap = totalOverlapOnTarget + this.getOverlapArea(
              target.x, target.y, targetW, targetH, x, y, placeW, placeH
            );
            if (overlap >= maxTargetOverlap) {
              targetsOK = false;
              break;
            }
          }
          if (!targetsOK) {
            attempts++;
            continue;
          }
        }
      }

      // ダミー同士の重なりチェック
      let totalOverlapOnNewItem = 0;
      for (const item of placedItems) {
        const itemW = ITEM_WIDTH * item.scale;
        const itemH = ITEM_HEIGHT * item.scale;
        totalOverlapOnNewItem += this.getOverlapArea(
          x,
          y,
          placeW,
          placeH,
          item.x,
          item.y,
          itemW,
          itemH
        );
      }

      const newItemOverlapRatio = totalOverlapOnNewItem / placeArea;

      if (totalOverlapOnNewItem < bestNewItemOverlap) {
        bestNewItemOverlap = totalOverlapOnNewItem;
        bestPosition = { x, y, onTopOfTarget };
      }

      if (newItemOverlapRatio <= MAX_OVERLAP_RATIO) {
        return { x, y, onTopOfTarget };
      }

      attempts++;
    }

    return bestPosition;
  }

  private createUI(): void {
    const { WIDTH, COLORS, FONTS } = GAME_CONFIG;

    // 上部UIバー背景（バウハウス：白、黒枠）
    const uiBg = this.add.graphics();
    uiBg.setDepth(10);
    uiBg.fillStyle(COLORS.WHITE, 0.75); // 透過で下のアイテムが少し見える
    uiBg.fillRect(0, 0, WIDTH, 250);
    uiBg.lineStyle(3, COLORS.BLACK, 1);
    uiBg.strokeRect(0, 0, WIDTH, 250);

    // ステージ表示（中央上）
    const stageNum = Math.floor((this.currentItemsPerStage - 5) / 10) + 1;
    this.stageText = this.add.text(WIDTH / 2, 15, `ステージ ${stageNum}`, {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    this.stageText.setOrigin(0.5, 0);
    this.stageText.setDepth(11);

    // スコア表示
    this.scoreText = this.add.text(20, 15, `スコア: ${this.score}`, {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    this.scoreText.setDepth(11);

    // タイマー表示
    this.timerText = this.add.text(WIDTH - 20, 15, `タイム: ${this.timeLeft}`, {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    this.timerText.setOrigin(1, 0);
    this.timerText.setDepth(11);

    // サウンド オン/オフボタン（タイムの下）
    const soundEnabled = this.registry.get('soundEnabled') ?? true;
    const soundKey = soundEnabled ? 'sound_on' : 'sound_off';
    this.soundToggleBtn = this.add.image(WIDTH - 20, 55, soundKey);
    this.soundToggleBtn.setOrigin(1, 0);
    this.soundToggleBtn.setDepth(11);
    this.soundToggleBtn.setInteractive({ useHandCursor: true });
    this.soundToggleBtn.on('pointerdown', () => this.toggleSound());

    // ターゲット表示
    this.createTargetDisplay();
  }

  private toggleSound(): void {
    // 現在の状態を取得してトグル
    const currentEnabled = this.registry.get('soundEnabled') ?? true;
    const newEnabled = !currentEnabled;
    
    // 状態を更新
    this.registry.set('soundEnabled', newEnabled);
    this.sound.mute = !newEnabled;
    try {
      localStorage.setItem('soundEnabled', String(newEnabled));
    } catch {
      // localStorage が使えない環境では無視
    }
    // オン=sound_on、オフ=sound_off
    this.soundToggleBtn.setTexture(newEnabled ? 'sound_on' : 'sound_off');
  }

  private createTargetDisplay(): void {
    const { WIDTH, FONTS, TARGET_COUNT } = GAME_CONFIG;

    // 「探すおっさん」ラベル
    const label = this.add.text(WIDTH / 2, 80, '探すおっさん', {
      fontFamily: FONTS.DEFAULT,
      fontSize: '18px',
      color: '#666666',
      fontStyle: 'bold',
    });
    label.setOrigin(0.5);
    label.setDepth(11);

    // ターゲットコンテナ
    this.targetContainer = this.add.container(WIDTH / 2, 240);
    this.targetContainer.setDepth(11);

    const spacing = 100;
    const startX = -spacing * ((TARGET_COUNT - 1) / 2);

    for (let i = 0; i < this.targetIds.length; i++) {
      const targetId = this.targetIds[i];
      const x = startX + i * spacing;

      // ターゲットアイテム画像（小さく、下揃え）
      const targetImage = this.add.image(x, 0, `item_${targetId}`);
      targetImage.setScale(0.35);
      targetImage.setOrigin(0.5, 1);
      targetImage.setName(`target_${targetId}`);

      this.targetContainer.add([targetImage]);
    }
  }

  private startTimer(): void {
    this.timerEvent = this.time.addEvent({
      delay: 1000,
      callback: this.updateTimer,
      callbackScope: this,
      loop: true,
    });
  }

  private updateTimer(): void {
    this.timeLeft--;
    this.timerText.setText(`タイム: ${this.timeLeft}`);

    // 残り30秒でターゲットのアニメーション開始
    if (this.timeLeft === GAME_CONFIG.TARGET_ANIM_START_TIME) {
      this.startTargetAnimations();
    }

    // 残り時間が少ない時の警告色（バウハウス赤）
    if (this.timeLeft <= 10) {
      this.timerText.setColor('#e74c3c');
    }

    // 時間切れ
    if (this.timeLeft <= 0) {
      this.endGame();
    }
  }

  /** 残り30秒以下でターゲットにアニメーションを付与 */
  private startTargetAnimations(): void {
    for (const item of this.items) {
      if (item.isTarget && item.sprite.active && !item.sprite.getData('targetAnimStarted')) {
        item.sprite.setData('targetAnimStarted', true);
        this.addTargetAnimation(item.sprite, item.sprite.scaleX);
      }
    }
  }

  /** 残り30秒以下ならアニメーション開始、そうでなければ待機 */
  private addTargetAnimationIfTimeUp(
    sprite: Phaser.GameObjects.Image,
    baseScale: number
  ): void {
    if (this.timeLeft <= GAME_CONFIG.TARGET_ANIM_START_TIME) {
      sprite.setData('targetAnimStarted', true);
      this.addTargetAnimation(sprite, baseScale);
    }
  }

  private onItemClick(
    sprite: Phaser.GameObjects.Image,
    itemId: number,
    isTarget: boolean
  ): void {
    const { SCORE_CORRECT, SCORE_WRONG } = GAME_CONFIG;

    if (isTarget && this.targetIds.includes(itemId)) {
      // 正解！
      this.sound.play('good', { volume: 0.5 });
      this.discoveredIdsThisSession.add(itemId);
      this.score += SCORE_CORRECT;
      this.foundTargets++;

      // アイテムを消す
      this.tweens.add({
        targets: sprite,
        scale: 0,
        alpha: 0,
        duration: 300,
        ease: 'Back.easeIn',
        onComplete: () => {
          sprite.destroy();
        },
      });

      // ターゲットリストから削除
      const targetIndex = this.targetIds.indexOf(itemId);
      const isLastTarget = this.targetIds.length === 1;
      
      // 最後のターゲットをタップしたらすぐにタップを無効化
      if (isLastTarget) {
        this.input.enabled = false;
      }
      
      if (targetIndex !== -1) {
        this.targetIds.splice(targetIndex, 1);

        // ターゲット表示を更新（見つかったものにチェックマーク）
        const targetImg = this.targetContainer.getByName(
          `target_${itemId}`
        ) as Phaser.GameObjects.Image;
        if (targetImg) {
          targetImg.setTint(0xffd700); // バウハウス黄
          targetImg.setAlpha(0.5);
        }
      }

      // 全ターゲット発見でステージクリア（最後のターゲット時はポイント表示のフェードアウト完了後に進む）
      this.showFeedback(
        sprite.x,
        sprite.y,
        true,
        isLastTarget ? () => this.onStageComplete() : undefined
      );
    } else {
      // 不正解
      this.sound.play('miss', { volume: 0.5 });
      this.score = Math.max(0, this.score + SCORE_WRONG);

      // 不正解エフェクト
      this.showFeedback(sprite.x, sprite.y, false);

      // アイテムを揺らす
      this.tweens.add({
        targets: sprite,
        x: sprite.x + 10,
        duration: 50,
        yoyo: true,
        repeat: 3,
      });
    }

    this.scoreText.setText(`スコア: ${this.score}`);
  }

  private showFeedback(
    x: number,
    y: number,
    isCorrect: boolean,
    onComplete?: () => void
  ): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    const text = isCorrect ? '+100' : '-20';
    const color = isCorrect ? '#ffffff' : '#ffffff'; // 白文字
    const strokeColor = isCorrect ? '#27ae60' : '#e74c3c'; // 縁取り：緑 or 赤

    const feedback = this.add.text(x, y, text, {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.LARGE_SIZE}px`,
      color: color,
      fontStyle: 'bold',
    });
    feedback.setOrigin(0.5);
    feedback.setDepth(20);
    feedback.setStroke(strokeColor, 6);

    // 正解時: パーティクルアニメーション + 円エフェクト
    if (isCorrect) {
      this.playParticleEffect(x, y);

      const circle = this.add.circle(x, y, 10, COLORS.YELLOW, 0.5);
      circle.setDepth(15);

      this.tweens.add({
        targets: circle,
        scale: 5,
        alpha: 0,
        duration: 500,
        onComplete: () => circle.destroy(),
      });
    }

    // テキストアニメーション（少し待ってからフェードアウト）
    this.tweens.add({
      targets: feedback,
      y: y - 50,
      alpha: 0,
      duration: 800,
      delay: 300,
      ease: 'Power2',
      onComplete: () => {
        feedback.destroy();
        onComplete?.();
      },
    });
  }

  /** Lottie パーティクルアニメーションを再生（particleKey 省略時は particle1） */
  private playParticleEffect(
    worldX: number,
    worldY: number,
    particleKey: 'particle1' | 'particle2' = 'particle1'
  ): void {
    const particleData = this.cache.json.get(particleKey) as object | undefined;
    if (!particleData) return;

    const canvas = this.sys.game.canvas;
    const container = canvas.parentElement;
    if (!container) return;

    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // ワールド座標をキャンバス表示座標に変換
    const scaleX = canvasRect.width / GAME_CONFIG.WIDTH;
    const scaleY = canvasRect.height / GAME_CONFIG.HEIGHT;
    const screenX = worldX * scaleX;
    const screenY = worldY * scaleY;

    // オーバーレイ（キャンバスと同位置・同サイズ）
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: absolute;
      left: ${canvasRect.left - containerRect.left}px;
      top: ${canvasRect.top - containerRect.top}px;
      width: ${canvasRect.width}px;
      height: ${canvasRect.height}px;
      pointer-events: none;
      overflow: hidden;
    `;
    container.appendChild(overlay);

    // パーティクル用コンテナ（particle2は3倍スケール）
    const baseSize = 200;
    const size =
      particleKey === 'particle2' ? baseSize * 3 : baseSize;
    const particleContainer = document.createElement('div');
    particleContainer.style.cssText = `
      position: absolute;
      left: ${screenX - size / 2}px;
      top: ${screenY - size / 2}px;
      width: ${size}px;
      height: ${size}px;
    `;
    overlay.appendChild(particleContainer);

    const anim = lottie.loadAnimation({
      container: particleContainer,
      renderer: 'canvas',
      animationData: particleData,
      loop: false,
      autoplay: true,
    });

    if (particleKey === 'particle2') {
      anim.setSpeed(1.2);
    }

    anim.addEventListener('complete', () => {
      anim.destroy();
      overlay.remove();
    });
  }

  private onStageComplete(): void {
    // ステージクリアボーナス
    const timeBonus = this.timeLeft * GAME_CONFIG.TIME_BONUS_MULTIPLIER;
    this.score += timeBonus;

    // アイテム数を次ステージ用に+10
    this.currentItemsPerStage += 10;

    // ステージ表示を更新
    const stageNum = Math.floor((this.currentItemsPerStage - 5) / 10) + 1;
    this.stageText.setText(`ステージ ${stageNum}`);

    // クリアごとに+5秒
    this.timeLeft += GAME_CONFIG.TIME_ADD_ON_CLEAR;
    this.timerText.setText(`タイム: ${this.timeLeft}`);
    if (this.timeLeft > 10) {
      this.timerText.setColor('#000000');
    }

    // 次のターゲットを選択して続行
    this.selectTargets();

    // ターゲット表示を更新
    this.targetContainer.destroy();
    this.createTargetDisplay();

    // 全アイテムをリセットして再配置
    this.resetAllItems();

    // クリア演出
    this.showStageComplete(timeBonus);
  }

  private resetAllItems(): void {
    // 既存のアイテムをすべて削除
    for (const item of this.items) {
      item.sprite.destroy();
    }
    this.items = [];

    // 新しくアイテムを配置
    this.placeItems();
  }

  private showStageComplete(timeBonus: number): void {
    const { WIDTH, HEIGHT, FONTS } = GAME_CONFIG;

    // クリア表示中はタップを受け付けない
    this.input.enabled = false;

    this.sound.play('clear', { volume: 0.5 });
    // クリア演出用パーティクル（画面中央）
    this.playParticleEffect(WIDTH / 2, HEIGHT / 2, 'particle2');

    const overlay = this.add.rectangle(
      WIDTH / 2,
      HEIGHT / 2,
      WIDTH,
      HEIGHT,
      0x000000,
      0.7
    );
    overlay.setDepth(50);

    const completeText = this.add.text(WIDTH / 2, HEIGHT / 2 - 50, 'クリア!', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.TITLE_SIZE}px`,
      color: '#ffd700',
      fontStyle: 'bold',
    });
    completeText.setOrigin(0.5);
    completeText.setDepth(51);

    const bonusText = this.add.text(
      WIDTH / 2,
      HEIGHT / 2 + 30,
      `タイムボーナス: +${timeBonus}`,
      {
        fontFamily: FONTS.DEFAULT,
        fontSize: `${FONTS.LARGE_SIZE}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      }
    );
    bonusText.setOrigin(0.5);
    bonusText.setDepth(51);
    bonusText.setStroke('#000000', 3);

    // 表示中はタイマーとアイテムアニメーションを個別に停止（pauseAllは新規Tweenも止めるため使わない）
    const activeTweens = this.tweens.getTweens();
    activeTweens.forEach((t) => t.pause());
    this.timerEvent.paused = true;

    // 表示2.5秒 → フェード0.5秒のTween（delay + duration）
    this.tweens.add({
      targets: [overlay, completeText, bonusText],
      alpha: 0,
      duration: 300,
      delay: 2000,
      onComplete: () => {
        overlay.destroy();
        completeText.destroy();
        bonusText.destroy();
        this.timerEvent.paused = false;
        activeTweens.forEach((t) => {
          if (!t.isDestroyed()) t.resume();
        });
        // タップを再度受け付ける
        this.input.enabled = true;
      },
    });
  }

  private endGame(): void {
    // タイマー停止
    if (this.timerEvent) {
      this.timerEvent.destroy();
    }

    // BGM停止、ゲームオーバーSE再生
    this.sound.stopByKey('bgm');
    this.sound.play('game_over');

    // 入力を無効化
    this.input.enabled = false;

    // 図鑑データをマージして保存してからゲーム終了
    const previousIds = this.registry.get(ENCYCLOPEDIA_DATA_KEY) as number[] | undefined;
    const mergedIds = [
      ...(previousIds ?? []),
      ...this.discoveredIdsThisSession,
    ];
    this.registry.set(ENCYCLOPEDIA_DATA_KEY, mergedIds);

    const dataJson = serializeDiscoveredIds(mergedIds);
    if (dataJson.length <= 1024) {
      callGameSave(dataJson, () => {
        callGameEnd(this.score, () => {
          this.scene.start('ResultScene', {
            score: this.score,
            foundTargets: this.foundTargets,
          });
        });
      });
    } else {
      // 1024文字超過時は保存せずに終了
      callGameEnd(this.score, () => {
        this.scene.start('ResultScene', {
          score: this.score,
          foundTargets: this.foundTargets,
        });
      });
    }
  }
}
